import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  useMemo,
} from "react";
import { getUserListFilters } from "../../../../services/users";

const UsersForDepartmentsContext = createContext();

const initialState = {
  users: [],
  meta: null,
  loading: false,
  error: null,
};

const CACHE_KEY_FULL = "__full__";
const CACHE_MAX_ENTRIES = 30;

const getCacheKey = (params) => {
  const keyword = params?.search?.keyword;
  if (keyword == null || String(keyword).trim() === "") return CACHE_KEY_FULL;
  return String(keyword).trim().toLowerCase();
};

export const UsersForDepartmentsProvider = ({ children }) => {
  const [state, setState] = useState(initialState);
  const fetchRequestIdRef = useRef(0);
  const cacheRef = useRef(new Map());

  const invalidateUserListCache = useCallback(() => {
    cacheRef.current.clear();
  }, []);

  const fetchUsers = useCallback(async (params = {}, options = {}) => {
    const { forceRefresh = false } = options;
    const cacheKey = getCacheKey(params);

    if (!forceRefresh && cacheRef.current.has(cacheKey)) {
      const cached = cacheRef.current.get(cacheKey);
      setState((prev) => ({
        ...prev,
        users: cached.users,
        meta: cached.meta,
        loading: false,
        error: null,
      }));
      return;
    }

    const requestId = ++fetchRequestIdRef.current;

    setState((prev) => ({
      ...prev,
      loading: true,
      error: null,
    }));

    try {
      const response = await getUserListFilters({
        page: params.page || 1,
        page_size: params.page_size || 50,
        order_by: params.order_by || ["full_name"],
        order_direction: params.order_direction || ["ASC"],
        search: params.search || null,
        filters: params.filters || null,
      });

      if (requestId !== fetchRequestIdRef.current) {
        setState((prev) => ({ ...prev, loading: false }));
        return;
      }

      const responseStatusCode = response?.statusCode || response?.status_code;
      const isValidResponse =
        responseStatusCode >= 200 && responseStatusCode < 300;

      if (!isValidResponse) {
        const errorMessage =
          response?.msgCode?.[0]?.description ||
          response?.msg_code?.[0]?.description ||
          "Invalid response status code";
        throw new Error(errorMessage);
      }

      const body = response?.body || [];
      const users = Array.isArray(body) ? body : [];
      const meta = response.meta || null;

      if (cacheRef.current.size >= CACHE_MAX_ENTRIES) {
        const firstKey = cacheRef.current.keys().next().value;
        if (firstKey != null) cacheRef.current.delete(firstKey);
      }
      cacheRef.current.set(cacheKey, { users, meta });

      setState((prev) => ({
        ...prev,
        users,
        meta,
        loading: false,
        error: null,
      }));
    } catch (error) {
      if (requestId !== fetchRequestIdRef.current) {
        setState((prev) => ({ ...prev, loading: false }));
        return;
      }

      console.error("Error fetching users for departments:", error);
      setState((prev) => ({
        ...prev,
        loading: false,
        error,
        users: [],
        meta: null,
      }));
    }
  }, []);

  const searchUsers = useCallback(
    async (keyword) => {
      if (!keyword || keyword.trim() === "") {
        await fetchUsers({ page_size: 50 });
        return;
      }

      await fetchUsers({
        search: { keyword: keyword.trim() },
        page_size: 50,
      });
    },
    [fetchUsers]
  );

  const adminOwnerOptions = useMemo(() => {
    return state.users.map((user) => {
      const userUuid = user.user_uuid || user.userUuid;
      const fullName = user.full_name || user.fullName || "";
      const role = user.role || "User";
      const email = user.email || "";

      return {
        label: `${fullName} - ${role}`,
        value: userUuid,
        userUuid: userUuid,
        fullName: fullName,
        email: email,
        role: role,
      };
    });
  }, [state.users]);

  const autocompleteUsers = useMemo(() => {
    return state.users.map((user) => {
      const userUuid = user.user_uuid || user.userUuid;
      const fullName = user.full_name || user.fullName || "";
      const email = user.email || "";

      return {
        user_uuid: userUuid,
        full_name: fullName,
        email: email,
        value: userUuid,
        label: fullName,
      };
    });
  }, [state.users]);

  const value = useMemo(
    () => ({
      adminOwnerOptions,
      autocompleteUsers,
      loading: state.loading,
      error: state.error,
      fetchUsers,
      searchUsers,
      invalidateUserListCache,
    }),
    [
      adminOwnerOptions,
      autocompleteUsers,
      state.loading,
      state.error,
      fetchUsers,
      searchUsers,
      invalidateUserListCache,
    ]
  );

  return (
    <UsersForDepartmentsContext.Provider value={value}>
      {children}
    </UsersForDepartmentsContext.Provider>
  );
};

export const useUsersForDepartments = () => {
  const context = useContext(UsersForDepartmentsContext);
  if (!context) {
    throw new Error(
      "useUsersForDepartments must be used within UsersForDepartmentsProvider"
    );
  }
  return context;
};
